import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { AlertTriangle, Info } from 'lucide-react';

const Index = () => {
  const [respiratoryRate, setRespiratoryRate] = useState<string>('');
  const [gcsScore, setGcsScore] = useState<string>('none');
  const [systolicBP, setSystolicBP] = useState<string>('');
  const [qsofaScore, setQsofaScore] = useState<number>(0);

  // Calculate qSOFA score in real-time
  useEffect(() => {
    let score = 0;

    // Respiratory rate ≥ 22/min
    if (respiratoryRate && parseInt(respiratoryRate) >= 22) {
      score += 1;
    }

    // Altered consciousness (GCS < 15)
    if (gcsScore !== 'none' && parseInt(gcsScore) < 15) {
      score += 1;
    }

    // Systolic BP ≤ 100 mmHg
    if (systolicBP && parseInt(systolicBP) <= 100) {
      score += 1;
    }

    setQsofaScore(score);
  }, [respiratoryRate, gcsScore, systolicBP]);

  const getScoreColor = (score: number) => {
    return score >= 2 ? 'text-red-600' : 'text-green-600';
  };

  const getScoreBgColor = (score: number) => {
    return score >= 2 ? 'bg-red-50 border-red-200' : 'bg-green-50 border-green-200';
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4">
      <div className="max-w-2xl mx-auto">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">qSOFA-Score Rechner</h1>
          <p className="text-gray-600">Schnelle Risikoeinschätzung bei Verdacht auf Sepsis</p>
        </div>

        <Card className="mb-6">
          <CardHeader>
            <CardTitle>Kriterien eingeben</CardTitle>
            <CardDescription>
              Bewerten Sie die drei qSOFA-Kriterien. Jedes erfüllte Kriterium gibt 1 Punkt.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Respiratory Rate */}
            <div className="space-y-2">
              <Label htmlFor="respiratory-rate">Atemfrequenz (pro Minute)</Label>
              <Input
                id="respiratory-rate"
                type="number"
                placeholder="z.B. 20"
                value={respiratoryRate}
                onChange={(e) => setRespiratoryRate(e.target.value)}
                className="w-full"
              />
              <p className="text-sm text-gray-500">
                ≥ 22/min = 1 Punkt {respiratoryRate && parseInt(respiratoryRate) >= 22 ? '✓' : ''}
              </p>
            </div>

            {/* GCS Score */}
            <div className="space-y-2">
              <Label htmlFor="gcs-score">Glasgow Coma Scale (GCS)</Label>
              <Select value={gcsScore} onValueChange={setGcsScore}>
                <SelectTrigger>
                  <SelectValue placeholder="GCS-Wert auswählen" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Bitte auswählen</SelectItem>
                  <SelectItem value="15">15 (normal)</SelectItem>
                  <SelectItem value="14">14</SelectItem>
                  <SelectItem value="13">13</SelectItem>
                  <SelectItem value="12">12</SelectItem>
                  <SelectItem value="11">11</SelectItem>
                  <SelectItem value="10">10</SelectItem>
                  <SelectItem value="9">9</SelectItem>
                  <SelectItem value="8">8</SelectItem>
                  <SelectItem value="7">7</SelectItem>
                  <SelectItem value="6">6</SelectItem>
                  <SelectItem value="5">5</SelectItem>
                  <SelectItem value="4">4</SelectItem>
                  <SelectItem value="3">3</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-sm text-gray-500">
                &lt; 15 = 1 Punkt {gcsScore !== 'none' && parseInt(gcsScore) < 15 ? '✓' : ''}
              </p>
            </div>

            {/* Systolic Blood Pressure */}
            <div className="space-y-2">
              <Label htmlFor="systolic-bp">Systolischer Blutdruck (mmHg)</Label>
              <Input
                id="systolic-bp"
                type="number"
                placeholder="z.B. 120"
                value={systolicBP}
                onChange={(e) => setSystolicBP(e.target.value)}
                className="w-full"
              />
              <p className="text-sm text-gray-500">
                ≤ 100 mmHg = 1 Punkt {systolicBP && parseInt(systolicBP) <= 100 ? '✓' : ''}
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Results */}
        <Card className={`mb-6 ${getScoreBgColor(qsofaScore)}`}>
          <CardHeader>
            <CardTitle className={`text-2xl ${getScoreColor(qsofaScore)}`}>
              qSOFA-Score: {qsofaScore} von 3
            </CardTitle>
          </CardHeader>
          <CardContent>
            {qsofaScore >= 2 ? (
              <Alert className="border-red-200 bg-red-50">
                <AlertTriangle className="h-4 w-4 text-red-600" />
                <AlertDescription className="text-red-800">
                  <strong>qSOFA ≥ 2: erhöhtes Risiko für ungünstigen Verlauf.</strong>
                  <br />
                  Weitere Abklärung, engmaschiges Monitoring und Sepsis-Diagnostik empfohlen.
                </AlertDescription>
              </Alert>
            ) : (
              <Alert className="border-green-200 bg-green-50">
                <Info className="h-4 w-4 text-green-600" />
                <AlertDescription className="text-green-800">
                  <strong>qSOFA &lt; 2: kein hohes Risiko nach qSOFA,</strong>
                  <br />
                  klinische Gesamteinschätzung bleibt notwendig.
                </AlertDescription>
              </Alert>
            )}
          </CardContent>
        </Card>

        {/* Information */}
        <Card className="bg-blue-50 border-blue-200">
          <CardContent className="pt-6">
            <div className="flex items-start space-x-3">
              <Info className="h-5 w-5 text-blue-600 mt-0.5" />
              <div className="text-sm text-blue-800">
                <p className="font-medium mb-1">Wichtiger Hinweis:</p>
                <p>
                  Der qSOFA-Score ist eine vereinfachte Form des SOFA-Scores zur schnellen 
                  Risikoeinschätzung bei Verdacht auf Sepsis (Sepsis-3, 2016). Er ersetzt 
                  keine vollständige diagnostische Beurteilung.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Index;
